import type { ColorScheme } from '@hover/blueprint/foundation';

import type { AiProduct } from '../types';

export type RgbColor = `rgb(${number}, ${number}, ${number})`;
export type HexColor = `#${string}`;
export type Color = RgbColor | HexColor;

export type RgbTuple = [r: number, g: number, b: number];

export const isHex = (color: string): color is HexColor =>
  color.startsWith('#');
export const isRgb = (color: string): color is RgbColor =>
  color.startsWith('rgb(');
export const isColor = (color: string): color is Color =>
  isHex(color) || isRgb(color);

function assertColor(color: string, label?: string): asserts color is Color {
  const labelText = label ? ` for \`${label}\`` : '';

  if (!isColor(color)) {
    throw new Error(`Unsupported color format${labelText}: ${color}`);
  }
}

/**
 * Parse a hexidecimal color string into a numeric RGB tuple
 *
 * @param color color string in hexidecimal format
 *
 * @returns RGB tuple
 */
export const parseHex = (color: string): RgbTuple => {
  if (!isHex(color) || (color.length !== 4 && color.length !== 7))
    throw new Error(`Unsupported color format: ${color}`);

  const hex = color.substring(1);
  const expanded =
    hex.length === 3
      ? color
          .split('')
          .map(char => char + char)
          .join('')
      : hex;

  return [
    parseInt(expanded.substring(0, 2), 16),
    parseInt(expanded.substring(2, 4), 16),
    parseInt(expanded.substring(4, 6), 16),
  ];
};

/**
 * Parse a CSS RGB color string value into a numeric RGB tuple
 *
 * @param color color string in RGB format
 *
 * @returns RGB tuple
 */
export const parseRgb = (color: string): RgbTuple => {
  if (!isRgb(color)) throw new Error(`Unsupported color format: ${color}`);

  const matches = color.match(/\d+/g);

  if (matches?.length !== 3)
    throw new Error(`Unsupported color format: ${color}`);

  return matches.map(Number) as RgbTuple;
};

const RED_WEIGHT = 0.2126;
const GREEN_WEIGHT = 0.7152;
const BLUE_WEIGHT = 0.0722;

/**
 * Calculate the relative brightness of a color using the WCAG 2.0 formula
 *
 * @see {@link https://www.w3.org/WAI/GL/wiki/Relative_luminance}
 * @see {@link https://www.w3.org/TR/WCAG20/#relativeluminancedef}
 *
 * @param color color string in hexidecimal or RGB format
 *
 * @returns decimal brightness value
 */
export const brightness = (color: string): number => {
  assertColor(color);

  const rgb = isHex(color) ? parseHex(color) : parseRgb(color);
  const [r, g, b] = rgb.map(channel => channel / 255);

  const calculatedLuminance =
    (r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4) * RED_WEIGHT +
    (g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4) * GREEN_WEIGHT +
    (b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4) * BLUE_WEIGHT;

  return calculatedLuminance;
};

/**
 * Calculate the contrast ratio between two colors using the WCAG 2.0 formula
 *
 * @see {@link https://www.w3.org/TR/WCAG20/#contrast-ratiodef}
 *
 * @param background background color string in hexidecimal or RGB format
 * @param foreground foreground color string in hexidecimal or RGB format
 *
 * @returns contrast ratio
 */
export const getContrastRatio = (
  background: string,
  foreground: string,
): number => {
  assertColor(background, 'background');
  assertColor(foreground, 'foreground');

  const backgroundBrightness = brightness(background);
  const foregroundBrightness = brightness(foreground);

  const ratio =
    (Math.max(backgroundBrightness, foregroundBrightness) + 0.05) /
    (Math.min(backgroundBrightness, foregroundBrightness) + 0.05);

  return Math.round(ratio * 100) / 100;
};

export type ForegroundMode = Extract<ColorScheme, 'light' | 'dark'>;

/**
 * Given a background color and a light and dark foreground color, pick the
 * foreground color with the higher contrast ratio
 *
 * @param background background color string in hexidecimal or RGB format
 * @param light light foreground color string in hexidecimal or RGB format
 * @param dark dark foreground color string in hexidecimal or RGB format
 * @param adjust arbitrary multiplier to adjust the contrast ratio comparison to
 * "favor" lights or darks. This is useful in some cases when the ratio is very
 * close and we want to force a consistent foreground color in that case. A
 * value greater than 1 favors a light foreground and less than 1 favors a dark
 * foreground.
 *
 * @returns tuple with supplied light or dark with the higher contrast ratio and
 * the foreground mode as `'light' | 'dark'`
 */
export const pickForegroundColor = (
  background: string,
  light: string,
  dark: string,
  adjust: number = 1,
) => {
  const mode: ForegroundMode =
    getContrastRatio(background, dark) >
    getContrastRatio(background, light) * adjust
      ? 'dark'
      : 'light';

  return { color: mode === 'light' ? light : dark, mode } as const;
};

export const productBrightnessWithDefault = ({ hex }: AiProduct) =>
  hex ? brightness(hex) : 0;
